import { scroll } from '@seek/ca-myprofile-utils';
import { useCallback, useEffect, useRef, type PropsWithChildren } from 'react';
import { useInView } from 'react-intersection-observer';
import { useLocation } from 'react-router-dom';

import { getComponent } from 'src/components/Profile/Loading/loadingCards';
import { useScrollContext } from 'src/components/Router/ScrollContext';
import { useCard } from 'src/components/Router/useCard';
import { useMode } from 'src/components/Router/useMode';
import { usePage } from 'src/components/Router/usePage';
import { READ, getScrollOptions } from 'src/consts';
import type { Card } from 'src/types';

interface Props {
  card?: Card;
  threshold?: number;
  componentProps?: Record<string, unknown>;
}

export const CardRoute = ({
  card,
  children,
  threshold = 0,
  componentProps,
}: PropsWithChildren<Props>) => {
  const scrollContext = useScrollContext();
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const { ref: inViewRef, inView } = useInView({ threshold });

  const currentCard = useCard();
  const mode = useMode(card);
  const page = usePage();
  const location = useLocation();

  const Component = card && getComponent({ card, page });
  const isCardInRoute = card === currentCard;

  const setRefs = useCallback(
    (node: HTMLSpanElement) => {
      wrapperRef.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  // Set card inView
  useEffect(() => {
    if (inView) {
      scrollContext.setScrollParams({ inView: card });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card, inView]);

  // Scroll to card
  useEffect(() => {
    if (
      card &&
      isCardInRoute &&
      mode === READ &&
      wrapperRef.current !== null &&
      scrollContext.inView !== currentCard
    ) {
      const isCypressTest =
        typeof window !== 'undefined' && typeof window.Cypress !== 'undefined';
      const options = getScrollOptions(page);

      setTimeout(() => {
        scroll(wrapperRef.current!, {
          ...options,
          duration: isCypressTest ? 1 : undefined,
          delay: isCypressTest ? 1 : options.delay,
          minDuration: isCypressTest ? 1 : options.minDuration,
        });
      }, 0);
    }
    // NOTE: Adding all dependancies to the array causes too many re-renders, which restarts the scroll animation, giving a jerky animation result. We observe `location` as there are cases where we do not scroll onto a new card, but still need to trigger scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCard, location]);

  return (
    <span ref={setRefs}>
      {Component && <Component {...componentProps} />}
      {!Component && children}
    </span>
  );
};
